/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import PropTypes from 'prop-types';

/* Importing the components from the material-ui library. */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers';

/* Importing components from the components folder. */
import AutoCompleteLocation from '../../components/AutoCompleteLocation';
import AutoCompletePatient from '../../components/AutoCompletePatient';
import FormHelpDialog from '../../components/FormHelpDialog';

/* Importing the functions from the Redux slices. */
import { getEncounter } from '../../features/encounter/encounterSlice';

/* Importing the functions from the formatEncounter and formatPatient files. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import { fEncounter } from '../../utils/Encounter/Encounter.utils.format';

/* Importing constants from Encounter.ValueSets.js file. */
import { ENCOUNTER_STATUS, V3_ACT_ENCOUNTER_CODE } from '../../utils/Encounter/Encounter.utils.ValueSets';

/* Defining the propTypes for the EncounterAdd component. */
EncounterDetails.propTypes = {
  patientID: PropTypes.string,
  isDetailsOpen: PropTypes.bool,
  encounterID: PropTypes.string,
  setIsDetailsOpen: PropTypes.func,
};

/**
 * Renders the EncounterDetails component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isDetailsOpen - Flag indicating whether the details dialog is open.
 * @param {string} props.patientID - The ID of the patient.
 * @param {string} props.encounterID - The ID of the encounter.
 * @param {function} props.setIsDetailsOpen - Function to set the state of isDetailsOpen.
 * @returns {JSX.Element} The rendered EncounterDetails component.
 */
export default function EncounterDetails(props) {
  const { isDetailsOpen, patientID, encounterID, setIsDetailsOpen } = props;

  const dispatch = useDispatch();

  const { Encounters } = useSelector((state) => state.encounter);
  const currentEncounter = Encounters.find((encounter) => encounter.id === encounterID);

  const [title, setTitle] = useState('Encounter');
  const [pageEncounter, setPageEncounter] = useState({
    id: '',
    status: '',
    class: '',
    subject: '',
    period_start: '',
    period_end: '',
    diagnosis_condition: '',
    location: '',
  });
  const [formHelpOpen, setFormHelpOpen] = useState(false);
  const [valueSet, setValueSet] = useState(null);

  const handleFormHelpOpen = (valueSet) => {
    setValueSet(valueSet);
    setFormHelpOpen(true);
  };

  const handleClose = useCallback(() => {
    setIsDetailsOpen(false);
    setPageEncounter({
      id: '',
      status: '',
      class: '',
      subject: '',
      period_start: '',
      period_end: '',
      diagnosis_condition: '',
      location: '',
    });
  }, [setIsDetailsOpen]);

  useEffect(() => {
    if (!currentEncounter) return;
    setPageEncounter(fEncounter(currentEncounter));
  }, [currentEncounter]);

  /* A function that is called when the page loads. */
  const initializeData = useCallback(async () => {
    if (encounterID) {
      setTitle(`Encounter  - ${encounterID}`);
      const hasEncounter = await fetchAndHandleData({
        dispatch,
        fetchAction: getEncounter,
        id: encounterID,
        errorMessage: 'No Encounter record found',
        navigateOnFailure: false,
      });

      if (!hasEncounter) {
        handleClose();
      }
    }
  }, [dispatch, encounterID, handleClose]);

  /* Calling the initializeData function when the component mounts and when the isEdit state changes. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <>
      <Dialog open={isDetailsOpen} onClose={handleClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
            <Grid item xs={12} md={12}>
              <Stack spacing={3}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ pt: 1 }}>
                    Details
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    select
                    label="Class"
                    value={pageEncounter.class || ''}
                    style={{ border: 'none' }}
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  >
                    {V3_ACT_ENCOUNTER_CODE.values.map((el) => (
                      <MenuItem key={el.code} value={el.code}>
                        {el.display}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    aria-label="help"
                    onClick={() => {
                      handleFormHelpOpen(V3_ACT_ENCOUNTER_CODE);
                    }}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={pageEncounter.status || ''}
                    style={{ border: 'none' }}
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  >
                    {ENCOUNTER_STATUS.values.map((el) => (
                      <MenuItem key={el.code} value={el.code}>
                        {el.display}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    aria-label="help"
                    onClick={() => {
                      handleFormHelpOpen(ENCOUNTER_STATUS);
                    }}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ pt: 1 }}>
                    Period
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <DateTimePicker
                    readOnly
                    label="Start"
                    value={pageEncounter.period_start !== '' ? moment(pageEncounter.period_start) : null}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />

                  <DateTimePicker
                    readOnly
                    label="End"
                    value={pageEncounter.period_end !== '' ? moment(pageEncounter.period_end) : null}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ pt: 1 }}>
                    Patient
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <AutoCompletePatient
                    fieldName={'subject'}
                    values={pageEncounter}
                    patientID={patientID}
                    label={'Subject'}
                    readOnly
                  />
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ pt: 1 }}>
                    Location
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <AutoCompleteLocation
                    fieldName="location"
                    values={pageEncounter}
                    locationID={pageEncounter?.location}
                    label="Location (Part Of)"
                    readOnly
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <FormHelpDialog isOpen={formHelpOpen} setIsOpen={setFormHelpOpen} valueSet={valueSet} />
    </>
  );
}
